import { delay as mswDelay, graphql, HttpResponse } from 'msw';
import { MOCK_CLIENT_ID } from '..';
import { FeatureFlagsResponse, getFeatureFlagsResponseMock } from '../featureFlagsMock';
import { createFeedItems } from '../../components/FeedView/mockData';
import { createKeptInspirations } from '../../components/BoardView/mockData';
import { getSavedItemsBrowserSuccessMock, getSavedItemsEmptyMock, getSavedItemsLimitedStockMock, getSavedItemsOutfitsBrowserSuccessMock, getSavedItemsOutfitsMswMock, getSavedItemsOutOfStockMock, getSavedItemsPageTwoMock, getSavedItemsPageZeroMock, getSavedItemsSuccessMock } from '../../components/SavedItemsLayout/mockData';
import { GetClientCapabilitiesAndBusinessLineQuery } from '../../utils/hooks/getClientCapabilitiesAndBusinessLine';
import { buildSavedOutfitsMock, fullyUnavailable, partiallyUnavailable, savedOutfitEdge, savedOutfitsMock } from '../../components/SavedOutfitsLayout/mockData';
import type { GetSavedItemsQuery, GetSavedItemsQueryVariables } from '../../utils/hooks/getSavedItems';
import type { GetSavedItemsOutfitsQuery, GetSavedItemsOutfitsQueryVariables } from '../../utils/hooks/getSavedItemsOutfits';
import type { DestroySavedOutfitMutation, DestroySavedOutfitMutationVariables, GetSavedOutfitsQuery, GetSavedOutfitsQueryVariables } from '../../utils/hooks/getSavedOutfits';
import type { GetClientConfigParamsQuery, GetClientConfigParamsQueryVariables } from '../../utils/hooks/getClientConfigParams';
import type { GetInspirationFeedQuery, GetInspirationFeedQueryVariables } from '../../utils/hooks/getInspirationFeed';
import type { GetKeptInspirationsQuery, GetKeptInspirationsQueryVariables } from '../../utils/hooks/getKeptInspirations';
import type { KeptInspirationSaveMutation, KeptInspirationSaveMutationVariables } from '../../utils/hooks/keptInspirationSave';
import type { KeptInspirationRemoveMutation, KeptInspirationRemoveMutationVariables } from '../../utils/hooks/keptInspirationRemove';
import type { KeptInspirationUpdateMutation, KeptInspirationUpdateMutationVariables } from '../../utils/hooks/keptInspirationUpdate';
import type { CloudinaryUploadSignatureCreateMutation, CloudinaryUploadSignatureCreateMutationVariables } from '../../utils/hooks/cloudinaryUploadSignatureCreate';
import { fixPreviewHandlers } from '../../features/fix-preview/mocks';
import { miniPdpProductHandler } from '../../features/quick-anchor-fix/mockHandlers';
import type { GetAutoShipChurnPreventionOfferQuery, GetAutoShipChurnPreventionOfferQueryVariables } from '../../components/FixCancellation/ChurnPrevention/GetAutoShipChurnPreventionOffer';
import { getAutoShipChurnPreventionOfferMock } from '../../components/FixCancellation/ChurnPrevention/mockData';
import { mockPreviousLooksHandler, defaultLookbook, defaultPreviousLooks, onboardedWithNoGalleries, defaultLookbookMissingFullBody } from '../../features/vision/gallery/mocks';
import type { GetLookbookGalleryQuery } from '../../features/vision/gallery/graphql/getLookbookGallery';
import type { ShopBrowseProductsQuery, ShopBrowseProductsQueryVariables } from '../../queries/shopBrowseProducts';
import { shopBrowseProductsMock } from '../factories/shopBrowseProducts';
import type { ShopProductTypeHierarchyQuery } from '../../queries/shopProductTypeHierarchy';
import { cloudinaryUpload, getClientInfo, getClientSelfie, getClientFullBody, clientSelfieUpdate, clientPhotoFullBodyCreate, visionOnboardingComplete } from '../../features/vision/onboarding/mocks/handlers';
import type { GetVisionStatusQuery, GetVisionStatusQueryVariables } from '../../utils/hooks/getVisionStatus';

/**
 * Test helper to determine if the query parameter is true or another value. Useful in
 * differenciating between e2e test state like error, empty, and etc.
 */
const hasQueryParam = (queryName: string, queryValue = 'true') => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(queryName) === queryValue;
};
const stitchfix = graphql.link(process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT || 'https://www.stitchfix.com/graphql-api/api/graphql');
const defaultInspirationFeedItems = createFeedItems(24);
let mockedKeptInspirations = createKeptInspirations(0);
const getSliceStart = (after: string | null | undefined, ids: string[]) => {
  if (!after) {
    return 0;
  }
  const index = ids.indexOf(after);
  return index === -1 ? 0 : index + 1;
};
const buildPageInfo = (ids: string[], start: number, count: number) => {
  const nodes = ids.slice(start, start + count);
  return {
    __typename: 'PageInfo' as const,
    endCursor: nodes.at(-1) ?? null,
    hasNextPage: start + count < ids.length,
    hasPreviousPage: start > 0,
    startCursor: nodes[0] ?? null
  };
};
const getClientCapabilitiesAndBusinessLine = stitchfix.query<GetClientCapabilitiesAndBusinessLineQuery>('getClientCapabilitiesAndBusinessLine', () => HttpResponse.json({
  data: {
    client: {
      __typename: 'Client' as const,
      id: MOCK_CLIENT_ID || '00000000',
      // Browser tests use `?businessLine=KIDS` and `?canShop=false`
      // for the negative Saved Outfits checks.
      businessLine: hasQueryParam('businessLine', 'KIDS') ? 'KIDS' : 'WOMENS',
      firstName: 'Buster',
      isAuthenticatedClient: true,
      capabilities: {
        canShop: !hasQueryParam('canShop', 'false'),
        __typename: 'ClientCapabilities' as const
      }
    }
  }
}));

/**
 * Browser tests pick a saved outfits state with `?savedOutfits=<state>`:
 * `empty`, `unavailable` (one partially and one fully unavailable outfit) or
 * `paged` (two outfits, then one more after "Load more").
 */
export const getSavedOutfits = stitchfix.query<GetSavedOutfitsQuery, GetSavedOutfitsQueryVariables>('GetSavedOutfits', ({
  variables
}) => {
  if (hasQueryParam('savedOutfits', 'empty')) {
    return HttpResponse.json({
      data: buildSavedOutfitsMock({
        edges: []
      })
    });
  }
  if (hasQueryParam('savedOutfits', 'unavailable')) {
    return HttpResponse.json({
      data: buildSavedOutfitsMock({
        edges: [savedOutfitEdge({
          id: 'partially-unavailable',
          availability: partiallyUnavailable
        }), savedOutfitEdge({
          id: 'fully-unavailable',
          availability: fullyUnavailable
        })]
      })
    });
  }
  if (hasQueryParam('savedOutfits', 'paged')) {
    if (variables.after) {
      return HttpResponse.json({
        data: buildSavedOutfitsMock({
          edges: [savedOutfitEdge({
            id: 'page-two'
          })]
        })
      });
    }
    return HttpResponse.json({
      data: buildSavedOutfitsMock({
        edges: [savedOutfitEdge({
          id: 'page-one-a'
        }), savedOutfitEdge({
          id: 'page-one-b'
        })],
        hasNextPage: true,
        endCursor: 'page-one-b'
      })
    });
  }
  return HttpResponse.json({
    data: savedOutfitsMock
  });
});
export const destroySavedOutfit = stitchfix.mutation<DestroySavedOutfitMutation, DestroySavedOutfitMutationVariables>('DestroySavedOutfit', ({
  variables
}) => HttpResponse.json({
  data: {
    listItemDestroy: {
      __typename: 'ListItemDestroyPayload',
      result: {
        __typename: 'List',
        id: variables.input.listItemId
      }
    }
  }
}));
export const getInspirationFeed = stitchfix.query<GetInspirationFeedQuery, GetInspirationFeedQueryVariables>('getInspirationFeed', ({
  variables
}) => {
  const first = variables.first ?? 20;
  const allIds = defaultInspirationFeedItems.map(item => item.contentId);
  const start = getSliceStart(variables.after, allIds);
  const nodes = defaultInspirationFeedItems.slice(start, start + first);
  return HttpResponse.json({
    data: {
      client: {
        __typename: 'Client',
        fixInspirationFeed: {
          __typename: 'FixInspirationFeedConnection',
          nodes,
          pageInfo: buildPageInfo(allIds, start, first)
        }
      }
    }
  });
});
export const getKeptInspirations = stitchfix.query<GetKeptInspirationsQuery, GetKeptInspirationsQueryVariables>('getKeptInspirations', ({
  variables
}) => {
  const first = variables.first ?? 20;
  const activeItems = mockedKeptInspirations.filter(item => !item.deleted);
  const allIds = activeItems.map(item => item.id);
  const start = getSliceStart(variables.after, allIds);
  const nodes = activeItems.slice(start, start + first);
  return HttpResponse.json({
    data: {
      client: {
        __typename: 'Client',
        keptInspirations: {
          __typename: 'KeptInspirationConnection',
          nodes,
          pageInfo: buildPageInfo(allIds, start, first)
        }
      }
    }
  });
});
export const keptInspirationSave = stitchfix.mutation<KeptInspirationSaveMutation, KeptInspirationSaveMutationVariables>('keptInspirationSave', ({
  variables
}) => {
  const {
    input
  } = variables;
  const nextItem = {
    __typename: 'KeptInspiration' as const,
    id: `kept-${mockedKeptInspirations.length + 1}`,
    contentId: input.imgId ?? input.cloudId ?? `upload-${Date.now()}`,
    contentType: 'IMAGE' as const,
    contentUrl: input.imgUrl ?? `https://res.cloudinary.com/stitch-fix/image/upload/${input.cloudId ?? 'client-upload'}.jpg`,
    note: input.note ?? null,
    deleted: false
  };
  mockedKeptInspirations = [...mockedKeptInspirations.filter(item => item.contentId !== nextItem.contentId), nextItem];
  return HttpResponse.json({
    data: {
      keptInspirationSave: {
        __typename: 'KeptInspirationSavePayload',
        result: {
          __typename: 'KeptInspirationMutationSuccess',
          success: true,
          keptInspiration: nextItem
        }
      }
    }
  });
});
export const keptInspirationRemove = stitchfix.mutation<KeptInspirationRemoveMutation, KeptInspirationRemoveMutationVariables>('keptInspirationRemove', ({
  variables
}) => {
  const removedItem = mockedKeptInspirations.find(item => item.id === variables.id);
  mockedKeptInspirations = mockedKeptInspirations.filter(item => item.id !== variables.id);
  return HttpResponse.json({
    data: {
      keptInspirationRemove: {
        __typename: 'KeptInspirationRemovePayload',
        result: {
          __typename: 'KeptInspirationMutationSuccess',
          success: true,
          keptInspiration: removedItem ?? {
            __typename: 'KeptInspiration',
            id: variables.id,
            contentId: '',
            contentType: 'IMAGE',
            contentUrl: '',
            note: null,
            deleted: true
          }
        }
      }
    }
  });
});
export const keptInspirationUpdate = stitchfix.mutation<KeptInspirationUpdateMutation, KeptInspirationUpdateMutationVariables>('keptInspirationUpdate', ({
  variables
}) => {
  let updatedItem = mockedKeptInspirations[0];
  mockedKeptInspirations = mockedKeptInspirations.map(item => {
    if (item.id !== variables.input.id) {
      return item;
    }
    updatedItem = {
      ...item,
      note: variables.input.note ?? null
    };
    return updatedItem;
  });
  return HttpResponse.json({
    data: {
      keptInspirationUpdate: {
        __typename: 'KeptInspirationUpdatePayload',
        result: {
          __typename: 'KeptInspirationMutationSuccess',
          success: true,
          keptInspiration: updatedItem
        }
      }
    }
  });
});
export const cloudinaryUploadSignatureCreate = stitchfix.mutation<CloudinaryUploadSignatureCreateMutation, CloudinaryUploadSignatureCreateMutationVariables>('cloudinaryUploadSignatureCreate', () => HttpResponse.json({
  data: {
    cloudinaryUploadSignatureCreate: {
      __typename: 'CloudinaryUploadSignatureCreatePayload',
      result: {
        __typename: 'CloudinaryUploadSignatureCreateSuccess',
        success: true,
        cloudinaryUploadSignature: {
          __typename: 'CloudinaryUploadSignature',
          signature: 'mock-signature',
          cloudName: 'stitch-fix',
          apiKey: 'mock-api-key',
          uploadPreset: 'mock-preset',
          type: 'private',
          timestamp: 1_700_000_000
        }
      }
    }
  }
}));
export const getSavedItems = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsSuccessMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsOutfits = stitchfix.query<GetSavedItemsOutfitsQuery, GetSavedItemsOutfitsQueryVariables>('getSavedItemsOutfits', () => {
  const {
    data
  } = getSavedItemsOutfitsMswMock;
  return HttpResponse.json({
    data
  });
});
export const getEmptySavedItems = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsEmptyMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsPageZero = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsPageZeroMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsOutOfStock = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsOutOfStockMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsPageTwo = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsPageTwoMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsLimitedStock = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data
  } = getSavedItemsLimitedStockMock;
  return HttpResponse.json({
    data
  });
});
export const getSavedItemsError = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => HttpResponse.json({
  errors: [{
    message: 'Something went wrong'
  }]
}));
export const getSavedItemsLoading = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', async () => {
  await mswDelay(1000);
  return HttpResponse.json({
    data: getSavedItemsSuccessMock.data
  }, {
    status: 200
  });
});
export const getSavedItemsForBrowserTest = stitchfix.query<GetSavedItemsQuery, GetSavedItemsQueryVariables>('getSavedItems', () => {
  const {
    data: sucessStateData
  } = getSavedItemsBrowserSuccessMock;
  if (hasQueryParam('showAvailableItems', 'false')) {
    const {
      data: outOfStockData
    } = getSavedItemsOutOfStockMock;
    return HttpResponse.json({
      data: outOfStockData
    });
  }
  if (hasQueryParam('page', '2')) {
    const {
      data: pageTwoData
    } = getSavedItemsPageTwoMock;
    return HttpResponse.json({
      data: pageTwoData
    });
  }
  return HttpResponse.json({
    data: sucessStateData
  });
});
export const getSavedItemsOutfitsForBrowserTest = stitchfix.query<GetSavedItemsOutfitsQuery, GetSavedItemsOutfitsQueryVariables>('getSavedItemsOutfits', () => {
  const {
    data: successStateData
  } = getSavedItemsOutfitsBrowserSuccessMock;
  return HttpResponse.json({
    data: successStateData
  });
});
export const getAutoShipChurnPreventionOfferModule = stitchfix.query<GetAutoShipChurnPreventionOfferQuery, GetAutoShipChurnPreventionOfferQueryVariables>('getAutoShipChurnPreventionOffer', () => {
  const {
    data
  } = getAutoShipChurnPreventionOfferMock();
  return HttpResponse.json({
    data
  });
});
export const shopBrowseProducts = stitchfix.query<ShopBrowseProductsQuery, ShopBrowseProductsQueryVariables>('ShopBrowseProducts', ({
  variables
}) => {
  const {
    color,
    sizeValue,
    personalized
  } = variables.input ?? {};
  const allResults = shopBrowseProductsMock.shopBrowseProducts.results;
  const filtered = allResults.filter(result => {
    const resultColor = result.variant?.color.name.toLowerCase();
    if (color?.length && (!resultColor || !color.includes(resultColor))) {
      return false;
    }
    if (sizeValue?.length && !result.availableSizes.some(s => sizeValue.includes(s.sizeValue))) {
      return false;
    }
    return true;
  });
  const facets = personalized ? [{
    __typename: 'ShopBrowseFacet' as const,
    name: 'color',
    buckets: [{
      __typename: 'ShopBrowseFacetBucket' as const,
      key: 'navy',
      docCount: 1
    }]
  }, {
    __typename: 'ShopBrowseFacet' as const,
    name: 'size_value',
    buckets: [{
      __typename: 'ShopBrowseFacetBucket' as const,
      key: 'M',
      docCount: 1
    }]
  }] : shopBrowseProductsMock.shopBrowseProducts.facets;
  return HttpResponse.json({
    data: {
      shopBrowseProducts: {
        ...shopBrowseProductsMock.shopBrowseProducts,
        results: filtered,
        totalCount: filtered.length,
        facets
      }
    }
  });
});
export const shopProductTypeHierarchy = stitchfix.query<ShopProductTypeHierarchyQuery>('ShopProductTypeHierarchy', () => HttpResponse.json({
  data: {
    shopProductTypeHierarchy: []
  }
}));
export const getFeatureFlags = stitchfix.query<FeatureFlagsResponse>('getFeatureFlags', () => {
  // Browser tests turn Saved Outfits off with `?savedOutfitsFlag=off`.
  if (hasQueryParam('savedOutfitsFlag', 'off')) {
    return HttpResponse.json({
      data: {
        ...getFeatureFlagsResponseMock,
        client: {
          ...getFeatureFlagsResponseMock.client,
          featureFlags: getFeatureFlagsResponseMock.client.featureFlags.map(flag => flag.name === 'client.web.enable_saved_outfits' ? {
            ...flag,
            enabled: false
          } : flag)
        }
      }
    });
  }
  return HttpResponse.json({
    data: getFeatureFlagsResponseMock
  });
});
export const getClientConfigParams = stitchfix.query<GetClientConfigParamsQuery, GetClientConfigParamsQueryVariables>('getClientConfigParams', () => HttpResponse.json({
  data: {
    __typename: 'Query',
    client: {
      __typename: 'Client',
      id: MOCK_CLIENT_ID || '00000000',
      businessLine: 'WOMENS',
      configParams: [{
        __typename: 'ConfigParam',
        name: 'aa.discount.fy26.q3.high_value_referrals',
        value: 'no_promo'
      }],
      capabilities: {
        __typename: 'ClientCapabilities',
        canShop: true,
        canScheduleFix: true
      }
    }
  }
}));
export const getVisionStatus = stitchfix.query<GetVisionStatusQuery, GetVisionStatusQueryVariables>('getVisionStatus', () => HttpResponse.json({
  data: {
    client: {
      __typename: 'Client' as const,
      businessLine: 'WOMENS' as const,
      vision: {
        __typename: 'Vision' as const,
        id: 'vision-id',
        canAccessVision: true,
        onboardingStatus: 'FULLY_ONBOARDED' as const,
        onboardingFailedMessage: null,
        onboardingVideo: {
          __typename: 'Video' as const,
          baseUrl: 'https://res.cloudinary.com/stitch-fix/video/upload/',
          mediaPath: 'v1/vision/onboarding/womens',
          width: 1080,
          height: 1920,
          posterImageUrl: null
        }
      }
    }
  }
}));
export const getVisionStatusNoAccess = stitchfix.query<GetVisionStatusQuery, GetVisionStatusQueryVariables>('getVisionStatus', () => HttpResponse.json({
  data: {
    client: {
      __typename: 'Client' as const,
      businessLine: 'WOMENS' as const,
      vision: {
        __typename: 'Vision' as const,
        id: 'vision-id',
        canAccessVision: false,
        onboardingStatus: 'NOT_YET_ONBOARDED' as const,
        onboardingFailedMessage: null,
        onboardingVideo: {
          __typename: 'Video' as const,
          baseUrl: 'https://res.cloudinary.com/stitch-fix/video/upload/',
          mediaPath: 'v1/vision/onboarding/womens',
          width: 1080,
          height: 1920,
          posterImageUrl: null
        }
      }
    }
  }
}));
export const getSavedOutfitsList = stitchfix.query('SavedOutfitsList', () => HttpResponse.json({
  data: {
    client: {
      __typename: 'Client',
      shoppableLists: [{
        __typename: 'List',
        id: 'saved-outfits',
        externalId: 'saved-outfits-ext',
        listType: 'internal_saved_outfits',
        listItems: []
      }]
    }
  }
}));
export const savedOutfitListCreate = stitchfix.mutation('SavedOutfitListCreate', () => HttpResponse.json({
  data: {
    listCreate: {
      result: {
        list: {
          id: 'saved-outfits',
          externalId: 'saved-outfits-ext',
          listType: 'internal_saved_outfits'
        }
      }
    }
  }
}));
export const savedOutfitListItemCreate = stitchfix.mutation('SavedOutfitListItemCreate', () => HttpResponse.json({
  data: {
    listItemCreate: {
      result: {
        listItem: {
          id: `list-item-${Date.now()}`,
          listItemId: `outfit-${Date.now()}`,
          listItemType: 'OUTFIT'
        }
      }
    }
  }
}));
export const savedOutfitListItemDestroy = stitchfix.mutation('SavedOutfitListItemDestroy', () => HttpResponse.json({
  data: {
    listItemDestroy: {
      result: {
        id: 'removed'
      }
    }
  }
}));
export const graphHandlers = [miniPdpProductHandler, getClientCapabilitiesAndBusinessLine, getInspirationFeed, getKeptInspirations, keptInspirationSave, keptInspirationRemove, keptInspirationUpdate, cloudinaryUploadSignatureCreate, ...fixPreviewHandlers, getSavedOutfits, destroySavedOutfit, getSavedOutfitsList, savedOutfitListCreate, savedOutfitListItemCreate, savedOutfitListItemDestroy, getSavedItems, getAutoShipChurnPreventionOfferModule, getFeatureFlags, getClientConfigParams, getVisionStatus, shopBrowseProducts, shopProductTypeHierarchy];
export const browserGraphHandlers = [miniPdpProductHandler, getClientCapabilitiesAndBusinessLine, getInspirationFeed, getKeptInspirations, keptInspirationSave, keptInspirationRemove, keptInspirationUpdate, cloudinaryUploadSignatureCreate, getSavedOutfits, destroySavedOutfit, getSavedOutfitsList, savedOutfitListCreate, savedOutfitListItemCreate, savedOutfitListItemDestroy, getSavedItemsForBrowserTest, getSavedItemsOutfitsForBrowserTest, getAutoShipChurnPreventionOfferModule, getFeatureFlags, getClientConfigParams, getVisionStatus,
// Use ?noGallery=true for no-lookbook state, ?noFullBody=true for missing full-body CTA
graphql.query('getLookbookGallery', () => {
  let mockData: GetLookbookGalleryQuery = defaultLookbook;
  if (hasQueryParam('noGallery')) mockData = onboardedWithNoGalleries as GetLookbookGalleryQuery;
  if (hasQueryParam('noFullBody')) mockData = defaultLookbookMissingFullBody as GetLookbookGalleryQuery;
  return HttpResponse.json({
    data: mockData
  });
}), mockPreviousLooksHandler(defaultPreviousLooks), cloudinaryUpload, getClientInfo, getClientSelfie, getClientFullBody, clientSelfieUpdate, clientPhotoFullBodyCreate, visionOnboardingComplete];